import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { colors } from "../theme";
import { useBets, useStravaProgress, formatTon, formatTimeLeft } from "../hooks/useBets";
import type { Bet } from "../hooks/useBets";
import { useUser } from "../hooks/useAuth";
import Badge from "../components/Badge";
import ProgressBar from "../components/ProgressBar";
import { cleanDescription } from "../utils/cleanDescription";

/* ---- Pull-to-refresh hook ---- */
function usePullToRefresh(onRefresh: () => Promise<void> | void) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pulling, setPulling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const THRESHOLD = 60;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (containerRef.current && containerRef.current.scrollTop <= 0) {
      startY.current = e.touches[0].clientY;
      setPulling(true);
    }
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pulling) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) setPullDistance(Math.min(dy * 0.5, 80));
    else { setPulling(false); setPullDistance(0); }
  }, [pulling]);

  const onTouchEnd = useCallback(async () => {
    if (!pulling) return;
    setPulling(false);
    if (pullDistance >= THRESHOLD) {
      setRefreshing(true);
      try { await onRefresh(); } finally { setRefreshing(false); }
    }
    setPullDistance(0);
  }, [pulling, pullDistance, onRefresh]);

  return { containerRef, pullDistance, refreshing, onTouchStart, onTouchMove, onTouchEnd };
}

export default function HomePage() {
  const { bets, stats, loading, error, refetch } = useBets();
  const navigate = useNavigate();
  const [liveProgressMap, setLiveProgressMap] = useState<Map<string, number>>(new Map());
  const activeBets = bets.filter((b) => ["live", "funded"].includes(b.status?.toLowerCase()));
  const resolvedBets = bets.filter((b) => ["resolved", "expired", "cancelled", "completed", "finished"].includes(b.status?.toLowerCase()));
  const pull = usePullToRefresh(refetch);

  useEffect(() => {
    if (loading) return;

    // VEN-34 / VEN-36: only Strava-tracked bets have a meaningful progress
    // value to fetch. Both-Agree bets used to trigger /strava/check-bet
    // requests that returned 0/empty data and rendered a useless 0% bar on
    // the Home card.
    const liveBets = bets.filter(
      (b) => b.status?.toLowerCase() === "live" && b.strava_bet_type,
    );
    if (liveBets.length === 0) {
      setLiveProgressMap(new Map());
      return;
    }

    let isCancelled = false;

    const fetchLiveProgress = async () => {
      const settled = await Promise.allSettled(
        liveBets.map(async (bet) => {
          const fitnessProvider = (import.meta.env.VITE_FITNESS_PROVIDER as string | undefined) || "healthbridge";
          const res = await api.get(`/fitness/${fitnessProvider}/check-bet/${bet.bet_id}`);
          const data = res.data || {};
          const percentage = Number(data.percentage ?? data.progress_pct ?? 0);
          return [bet.bet_id, percentage] as const;
        }),
      );

      if (isCancelled) return;

      const next = new Map<string, number>();
      for (const item of settled) {
        if (item.status === "fulfilled") {
          next.set(item.value[0], item.value[1]);
        }
      }
      setLiveProgressMap(next);
    };

    void fetchLiveProgress();

    return () => {
      isCancelled = true;
    };
  }, [bets, loading]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 14, color: colors.grayLight }}>Loading bets...</div>
      </div>
    );
  }

  const isEmpty = activeBets.length === 0 && resolvedBets.length === 0;

  if (isEmpty && error) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center", gap: 12 }}>
        <div style={{ fontSize: 48 }}>{"\u26A0\uFE0F"}</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: colors.white }}>Could not load your bets</div>
        <div style={{ fontSize: 13, color: colors.grayLight }}>{error}</div>
        <button
          onClick={refetch}
          style={{ marginTop: 8, padding: "10px 24px", borderRadius: 10, border: "none", background: colors.primary, color: colors.bg, fontSize: 14, fontWeight: 700, cursor: "pointer" }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isEmpty) {
    return <EmptyState onCreateClick={() => navigate("/create")} />;
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>SideBets 🔥</div>
        {stats && (
          <div
            style={{
              padding: "4px 10px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              background: colors.surfaceLight,
              color: colors.primary,
            }}
          >
            {stats.wins}W · {stats.losses}L
          </div>
        )}
      </div>

      {/* Scrollable content with pull-to-refresh */}
      <div
        ref={pull.containerRef}
        onTouchStart={pull.onTouchStart}
        onTouchMove={pull.onTouchMove}
        onTouchEnd={pull.onTouchEnd}
        style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px" }}
      >
        {/* Pull indicator */}
        {(pull.pullDistance > 0 || pull.refreshing) && (
          <div style={{ textAlign: "center", padding: "8px 0", fontSize: 12, color: colors.grayLight, transition: "opacity 0.2s" }}>
            {pull.refreshing ? "Refreshing..." : pull.pullDistance >= 60 ? "Release to refresh" : "Pull down to refresh"}
          </div>
        )}
        {/* ACTIVE section */}
        {activeBets.length > 0 && (
          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.gray, textTransform: "uppercase", marginBottom: 10 }}>
              Active
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {activeBets.map((bet) => (
                <ActiveBetCard
                  key={bet.bet_id}
                  bet={bet}
                  progressPct={liveProgressMap.get(bet.bet_id)}
                  onClick={() => navigate(`/bet/${bet.bet_id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* RESULTS section */}
        {resolvedBets.length > 0 && (
          <section style={{ marginTop: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.gray, textTransform: "uppercase", marginBottom: 10 }}>
              Results
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {resolvedBets.map((bet) => (
                <ResultRow key={bet.bet_id} bet={bet} onClick={() => navigate(`/bet/${bet.bet_id}`)} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/* ---- Empty State ---- */

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 64, marginBottom: 16 }}>🎯</div>
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Ready to bet? 🚀</div>
      <div
        style={{
          fontSize: 14,
          color: colors.grayLight,
          lineHeight: 1.6,
          marginBottom: 24,
        }}
      >
        Challenge a friend to something real.
        <br />
        Fitness. HealthBridge-verified.
        <br />
        Money on the line.
      </div>
      <button
        onClick={onCreateClick}
        style={{
          background: colors.primary,
          color: colors.bg,
          border: "none",
          padding: "14px 32px",
          borderRadius: 14,
          fontSize: 15,
          fontWeight: 700,
          textTransform: "uppercase",
        }}
      >
        Create First Bet 🔥
      </button>
    </div>
  );
}

/* ---- Active Bet Card ---- */

function ActiveBetCard({ bet, progressPct, onClick }: { bet: Bet; progressPct?: number; onClick: () => void }) {
  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const title = cleanedDescription;
  const isLive = bet.status?.toLowerCase() === "live";

  let opponentName = "opponent";
  if (bet.opponent_name) {
    opponentName = bet.opponent_name.replace(/^@/, "");
  } else if (bet.bet_mode === "pile_on") {
    const totalPeople = (bet.for_count || 0) + (bet.against_count || 0);
    if (totalPeople > 0) {
      opponentName = `${totalPeople} players`;
    }
  }

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: 16,
        cursor: "pointer",
        color: colors.white,
      }}
    >
      {/* Top row: emoji + title + badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 20 }}>{emoji}</span>
          <span style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title}
          </span>
        </div>
        <Badge status={isLive ? "live" : "funded"} />
      </div>

      {/* Middle: vs opponent */}
      <div style={{ fontSize: 13, color: colors.grayLight, marginBottom: isLive ? 10 : 0 }}>
        {bet.bet_mode === "pile_on" ? opponentName : `vs @${opponentName}`}
      </div>

      {/* LIVE: progress + time. VEN-34: Both-Agree bets have no Strava
          progress, so we render the time-only row below instead of an empty
          0% progress bar. */}
      {isLive && bet.strava_bet_type && <LiveProgress bet={bet} progressPct={progressPct} />}
      {isLive && !bet.strava_bet_type && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <span style={{ fontSize: 13, color: colors.grayLight }}>Awaiting agreement</span>
          <span style={{ fontSize: 12, color: colors.grayLight }}>{formatTimeLeft(bet.expires_at)}</span>
        </div>
      )}

      {/* WAITING: locked amount + countdown */}
      {!isLive && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
          <span style={{ fontSize: 13, color: colors.amber }}>
            {bet.bet_mode === "pile_on"
              ? `⏳ Waiting · ${formatTon(bet.total_pot || bet.amount)} pot`
              : `⏳ Waiting · ${formatTon(bet.amount)} locked`}
          </span>
          <span style={{ fontSize: 12, color: colors.grayLight }}>
            {formatTimeLeft(bet.expires_at)}
          </span>
        </div>
      )}
    </button>
  );
}

/* ---- Live Progress (fetches Strava data) ---- */

function LiveProgress({ bet, progressPct }: { bet: Bet; progressPct?: number }) {
  const progress = useStravaProgress(bet.bet_id);
  const pct = progressPct ?? progress?.percentage ?? 0;
  const current = progress ? `${progress.current_value}` : "...";
  const target = progress ? `${progress.target_value}` : "...";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <span style={{ fontSize: 18, fontWeight: 800 }}>{Math.round(pct)}%</span>
        <span style={{ fontSize: 12, color: colors.grayLight }}>{formatTimeLeft(bet.expires_at)}</span>
      </div>
      <ProgressBar percentage={pct} leftLabel={current} rightLabel={target} />
    </div>
  );
}

/* ---- Result Row ---- */

function ResultRow({ bet, onClick }: { bet: Bet; onClick: () => void }) {
  const user = useUser();
  const isCancelledOrExpired = ["cancelled", "expired"].includes(bet.status?.toLowerCase());
  const won = !isCancelledOrExpired && !!user && (() => {
    if (bet.bet_mode === "pile_on" && bet.winner_side) {
      let mySide: string | null = null;
      if (user.telegram_id === bet.challenger_telegram_id) {
        mySide = bet.pile_on_type === "dare" ? "against" : "for";
      } else if (user.telegram_id === (bet as any).target_telegram_id) {
        mySide = "for";
      }
      if (mySide) return mySide === bet.winner_side;
    }
    return bet.winner_id === user.id || bet.winner_id === user.telegram_id;
  })();
  const cleanedDescription = cleanDescription(bet.description);
  const emoji = getActivityEmoji(cleanedDescription);
  const tonAmount = bet.amount / 1_000_000_000;

  let amountText: string;
  let amountColor: string;
  if (isCancelledOrExpired) {
    amountText = "Refunded";
    amountColor = colors.gray;
  } else {
    const sign = won ? "+" : "-";
    amountText = `${sign}${tonAmount % 1 === 0 ? tonAmount.toFixed(0) : tonAmount.toFixed(1)} TON`;
    amountColor = won ? colors.success : colors.danger;
  }

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: `1px solid ${colors.border}`,
        background: "transparent",
        color: colors.white,
        cursor: "pointer",
        border: "none",
        borderBlockEnd: `1px solid ${colors.border}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 18 }}>{emoji}</span>
        <span style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {cleanedDescription}
        </span>
      </div>
      <span style={{ fontSize: 14, fontWeight: 700, color: amountColor, whiteSpace: "nowrap" }}>
        {amountText}
      </span>
    </button>
  );
}

/* ---- Helpers ---- */

function getActivityEmoji(description: string | undefined | null): string {
  if (!description) return "🎯";
  const d = description.toLowerCase();
  if (d.includes("run")) return "🏃";
  if (d.includes("cycle") || d.includes("ride") || d.includes("bike")) return "🚴";
  if (d.includes("swim")) return "🏊";
  if (d.includes("workout") || d.includes("gym") || d.includes("session")) return "💪";
  if (d.includes("step")) return "👟";
  return "🎯";
}
